import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Language } from '../types';
import { LanguageProvider, useLanguage } from '../contexts/LanguageContext';
import { SurveyProvider } from '../contexts/SurveyContext';
import { FirebaseProvider, useFirebase } from '../contexts/FirebaseContext';
import { IntroCard } from './IntroCard';
import { QuestionCard } from './QuestionCard';
import { ThankYouCard } from './ThankYouCard';
import { useSurvey } from '../contexts/SurveyContext';
import { APP_CONFIG } from '../config/settings';
import {
  getQueuedSubmissions,
  queueSubmission,
} from '../utils/submissionQueue';

type PollState = 'intro' | 'survey' | 'complete';

function PollContent() {
  const [pollState, setPollState] = useState<PollState>('intro');
  const [wasQueued, setWasQueued] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const { isComplete, resetSurvey, completeSurvey } = useSurvey();
  const { submitSurveyResponse, flushQueue } = useFirebase();
  const { setLanguage } = useLanguage();

  // Auto-advance to complete state when survey is done and submit in background
  React.useEffect(() => {
    if (isComplete && !hasSubmitted) {
      setHasSubmitted(true);

      // Move to thank you screen immediately
      setPollState('complete');

      // Submit in background
      const submitResponse = async () => {
        try {
          const response = completeSurvey();
          const queue = getQueuedSubmissions();

          if (queue.length > 0) {
            // Queue exists - add new answers to it and attempt to flush all
            console.log('Queue exists, adding new response and flushing');
            queueSubmission(response);
            await flushQueue();

            // Check if queue was successfully flushed
            const remainingQueue = getQueuedSubmissions();
            if (remainingQueue.length > 0) {
              setWasQueued(true);
              console.log(
                `Queue flush incomplete, ${remainingQueue.length} items remain`
              );
            } else {
              console.log('Queue flushed successfully');
            }
          } else {
            // No queue - submit directly
            const result = await submitSurveyResponse(response);

            if (result.queued) {
              setWasQueued(true);
              console.log('Survey queued for later submission');
            } else {
              console.log('Survey submitted successfully');
            }
          }
        } catch (error) {
          console.error('Unexpected error during submission:', error);
          setWasQueued(true);
        }
      };

      submitResponse();
    }
  }, [
    isComplete,
    hasSubmitted,
    completeSurvey,
    submitSurveyResponse,
    flushQueue,
  ]);

  const handleStart = () => {
    setPollState('survey');
  };

  const resetPoll = () => {
    resetSurvey(); // Reset survey data
    setWasQueued(false); // Reset queued flag
    setHasSubmitted(false); // Reset submission flag
    setLanguage(APP_CONFIG.DEFAULT_LANGUAGE); // Reset to default language
    setPollState('intro'); // Back to start screen
  };

  const renderContent = () => {
    switch (pollState) {
      case 'intro':
        return <IntroCard onStart={handleStart} />;
      case 'survey':
        return <QuestionCard onRestart={resetPoll} />;
      case 'complete':
        return <ThankYouCard onRestart={resetPoll} wasQueued={wasQueued} />;
      default:
        return <IntroCard onStart={handleStart} />;
    }
  };

  return renderContent();
}

function PollWrapper() {
  const { getTotalQuestions, language } = useLanguage();
  const totalQuestions = getTotalQuestions();

  return (
    <SurveyProvider totalQuestions={totalQuestions} language={language}>
      <PollContent />
    </SurveyProvider>
  );
}

function PollPage() {
  const { lang } = useParams<{ lang?: Language }>();
  // Use URL param if present, otherwise use DEFAULT_LANGUAGE
  const currentLang = lang || APP_CONFIG.DEFAULT_LANGUAGE;

  return (
    <FirebaseProvider>
      <LanguageProvider initialLanguage={currentLang}>
        <PollWrapper />
      </LanguageProvider>
    </FirebaseProvider>
  );
}

export default PollPage;
